import {
  defineSchemaSource,
  type FieldModalInput,
  type FieldTypeOption,
  type SchemaSource,
} from '../types/SchemaSource';
import type {
  CollectionBackend,
  FieldActions,
  ItemPermissions,
  LoadedSchema,
  Schema,
  SchemaPermissions,
} from '../types/Schema';
import type { Field } from '../types/Field';
import type { FieldTypeDefinition } from '../types/FieldTypeDefinition';
import type { OffsetQuery } from '../hooks/paginationModeConfig';
import { InMemoryBackend } from '../InMemoryBackend';

const ALL_PERMISSIONS: SchemaPermissions = {
  createField: true,
  updateField: true,
  deleteField: true,
};

// The types the add/edit modal offers by default (the ones @wix/data can create).
const DEFAULT_FIELD_TYPE_OPTIONS: FieldTypeOption[] = [
  { patternsType: 'SHORT_TEXT' },
  { patternsType: 'NUMBER' },
  { patternsType: 'BOOLEAN' },
  { patternsType: 'DATE' },
];

export interface InMemorySchemaSource<T> {
  /** The source to pass anywhere a schema source is accepted. */
  source: SchemaSource<T>;
  /** The in-memory backend holding the rows — read or mutate them directly. */
  backend: InMemoryBackend<T>;
}

export interface InMemorySchemaSourceConfig<T> {
  collectionId: string;
  /** The collection's fields (keyed internally by `field.id`). */
  fields: Field[];
  /** Seed rows the backend serves via `find`/`get`. */
  items?: T[];
  /** Defaults to the first field's id, else `idField`. */
  displayField?: string;
  /** Defaults to `_id`. */
  idField?: string;
  /** Defaults to all-true (add/edit/delete allowed). */
  schemaPermissions?: SchemaPermissions;
  itemPermissions?: ItemPermissions;
  /** What the add/edit field modal shows. `types` defaults to
   *  text/number/boolean/date. */
  fieldModal?: FieldModalInput;
  /** Consumer-defined field types, resolved by fields with `type: 'CUSTOM'`. */
  fieldTypes?: FieldTypeDefinition[];
  supportsPii?: boolean;
  /** Sources for referenced collections, keyed by referenced collection id. */
  references?: Record<string, SchemaSource<any>>;
  getReferencedCollectionSource?: (
    collectionId: string,
  ) => SchemaSource<any> | undefined;
}

/**
 * Builds a real `SchemaSource<T>` backed by in-memory state — no data source (no
 * `@wix/data`, no network). Its backend `find` serves the seeded `items`, and
 * its `fieldActions` mutate the in-memory schema and return the updated fields
 * map, so the create-new-field import flow works end to end (the created column
 * surfaces via the schema-source merge).
 *
 * Returns `{ source, backend }`. Pass `source` anywhere a `SchemaSource` is
 * accepted — a `@wix/patterns/schema` collection hook, or `<Table
 * fieldsSource={...} />` (which the import reads from). Use `backend` to read or
 * assert on the in-memory rows directly.
 */
export function createInMemorySchemaSource<
  T extends Record<string, unknown> = Record<string, unknown>,
>(config: InMemorySchemaSourceConfig<T>): InMemorySchemaSource<T> {
  const {
    collectionId,
    idField = '_id',
    displayField = config.fields[0]?.id ?? idField,
    schemaPermissions = ALL_PERMISSIONS,
    itemPermissions,
    fieldModal,
    fieldTypes,
    supportsPii = false,
    references,
    getReferencedCollectionSource,
  } = config;

  // Mutable in-memory state shared by the backend, schema, and field actions.
  const fields: Schema['fields'] = {};
  config.fields.forEach((field) => {
    fields[field.id] = field;
  });

  const schema: Schema = {
    id: collectionId,
    fields,
    displayField,
    idField,
    schemaPermissions,
    ...(itemPermissions && { itemPermissions }),
  };

  const keyOf = (item: T): string =>
    String((item as Record<string, unknown>)[idField] ?? '');
  // A fresh copy each mutation, so the schema-source merge sees a new map.
  const fieldsSnapshot = (): Schema['fields'] => ({ ...fields });

  // The shared `InMemoryBackend` holds the rows and runs the query logic
  // (ordering, filtering, pagination). The `collectionBackend` below adapts it
  // to the source-neutral contract. Mutations write straight to `backend.map`
  // and reads use the synchronous surface, so operations resolve without the
  // util's simulated async delay (which relies on timers the test suite freezes).
  const seed = config.items ?? [];
  const backend = new InMemoryBackend<T>({
    paginationMode: 'offset',
    total: seed.length,
    createOne: (index) => seed[index],
    itemKey: keyOf,
  });

  const collectionBackend: CollectionBackend<T> = {
    paginationMode: 'offset',
    find: async (query: OffsetQuery) => {
      const offset = query.offset ?? 0;
      const { items, total = 0 } = backend.fetchDataSync(query);
      return { items, total, hasNext: offset + items.length < total };
    },
    get: async (entityId) => {
      const item = backend.get(entityId);
      if (!item) {
        throw new Error(`Item "${entityId}" not found`);
      }
      return item;
    },
    create: async (entity) => {
      const created = { ...entity } as T;
      backend.map.set(keyOf(created), created);
      return created;
    },
    update: async (entity) => {
      backend.map.set(keyOf(entity), entity);
      return entity;
    },
    delete: async (entityId) => {
      backend.map.delete(entityId);
    },
    bulkDelete: async (entityIds) => {
      entityIds.forEach((entityId) => backend.map.delete(entityId));
    },
    bulkInsert: async (entities) => {
      entities.forEach((entity) => {
        const created = { ...entity } as T;
        backend.map.set(keyOf(created), created);
      });
      return { succeeded: entities.length, failed: 0 };
    },
  };

  const fieldActions: FieldActions = {
    addField: async (field) => {
      fields[field.id] = field;
      return fieldsSnapshot();
    },
    addFields: async (newFields) => {
      newFields.forEach((field) => {
        fields[field.id] = field;
      });
      return fieldsSnapshot();
    },
    editField: async (fieldId, updates) => {
      const current = fields[fieldId];
      if (current) {
        fields[fieldId] = { ...current, ...updates } as Field;
      }
      return fieldsSnapshot();
    },
    deleteField: async (fieldId) => {
      Reflect.deleteProperty(fields, fieldId);
      return fieldsSnapshot();
    },
    renameField: async (fieldId, newName) => {
      const current = fields[fieldId];
      if (current) {
        fields[fieldId] = { ...current, displayName: newName } as Field;
      }
    },
    makePrimary: async (fieldId) => {
      schema.displayField = fieldId;
      return fieldsSnapshot();
    },
  };

  const loadSchema = async (): Promise<LoadedSchema<T>> => ({
    schema,
    backend: collectionBackend,
    fieldActions,
  });

  const source = defineSchemaSource<T>({
    collectionId,
    loadSchema,
    fieldModal: {
      ...fieldModal,
      types: fieldModal?.types ?? DEFAULT_FIELD_TYPE_OPTIONS,
    },
    fieldTypes,
    supportsPii,
    ...(references && { references }),
    ...(getReferencedCollectionSource && { getReferencedCollectionSource }),
  });

  return { source, backend };
}
